import React from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Radio,
  Activity,
  Cpu,
  Shield,
  Key,
  Lock,
  BookOpen,
  Terminal,
  Heart,
  Thermometer,
  Zap,
  ShieldCheck,
  Database,
  Smartphone,
  ChevronRight,
  Monitor
} from "lucide-react";

import { motion } from "framer-motion";
import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./Documentation.css";

const Documentation = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const quickSteps = [
    {
      title: "1. Create Account",
      icon: Users,
      desc: "Sign up as a patient to track your health, or as a doctor to monitor your patients securely."
    },
    {
      title: "2. Link Devices",
      icon: Radio,
      desc: "Connect your smart health sensors (like Heart Rate or SpO2 monitors) to your Medico account."
    },
    {
      title: "3. Live Monitoring",
      icon: Activity,
      desc: "Watch your health data flow into your dashboard in real-time. Everything is automatic."
    }
  ];

  const dashboardFeatures = [
    {
      title: "Health Ticker",
      icon: Activity,
      desc: "A live scrolling bar that shows your current vitals at a glance, always visible at the top."
    },
    {
      title: "Visual Charts",
      icon: Zap,
      desc: "Easy-to-read graphs that show how your health trends move over the last 24 hours."
    },
    {
      title: "Risk Alerts",
      icon: Shield,
      desc: "Automatic warnings that pop up if your heart rate or oxygen levels go outside safe ranges."
    },
    {
      title: "Clinical History",
      icon: Database,
      desc: "A full record of all your past readings, securely saved so you can show them to your doctor."
    }
  ];

  const hardwareGuide = [
    {
      title: "HEART_RATE_NODE",
      sensor: "MAX30102",
      desc: "Measures blood oxygen (SpO2) and pulse with high precision. Best placed on the fingertip.",
      icon: Heart
    },
    {
      title: "CARDIAC_ECG_NODE",
      sensor: "AD8232",
      desc: "Captures the electrical activity of your heart. Uses three simple pads placed on the chest.",
      icon: Zap
    },
    {
      title: "THERMAL_NODE",
      sensor: "DS18B20",
      desc: "Tracks body temperature continuously. Accurate to within 0.1 degrees Celsius.",
      icon: Thermometer
    }
  ];

  const securityNodes = [
    {
      title: "HIPAA Compliant",
      icon: ShieldCheck,
      desc: "We follow international medical standards to keep your records private and safe."
    },
    {
      title: "Total Encryption",
      icon: Lock,
      desc: "Your data is scrambled with military-grade codes so only you and your doctor can read it."
    },
    {
      title: "Safe Login",
      icon: Key,
      desc: "Secure email verification and role-based access mean your profile is always protected."
    },
    {
      title: "Data Sovereignty",
      icon: Database,
      desc: "You own your data. You choose who can see your health reports and for how long."
    }
  ];

  const codeSnippet = `// Medico IoT Setup (ESP32)
#include <MedicoClient.h>

void setup() {
  Medico.begin("YOUR_AUTH_TOKEN");
  Medico.connectWiFi("SSID", "PASS");
}

void loop() {
  float heartRate = sensor.readPulse();
  Medico.sendVitals(heartRate, 98.5); 
  delay(1000); 
}`;

  return (
    <div className="doc-page-shell">
      <Navigation />

      <div className="doc-tactical-grid"></div>
      <div className="doc-scanline-overlay"></div>

      <motion.main
        className="doc-content-hub"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* HERO */}
        <motion.header className="doc-hero-sector" variants={itemVariants}>
          <div className="doc-hud-badge">
            <BookOpen size={14} />
            <span>PLATFORM_GUIDE_V2.4</span>
          </div>

          <h1 className="doc-hero-title">
            How to use <span className="doc-highlight">Medico</span>
          </h1>

          <p className="doc-hero-subtitle">
            A simple, step-by-step guide to setting up your clinical dashboard and connecting your health devices.
          </p>
        </motion.header>

        {/* QUICK START */}
        <section className="doc-quickstart-sector">
          <motion.h2 className="doc-sector-label" variants={itemVariants}>
            01. QUICK_START
          </motion.h2>

          <div className="doc-start-grid">
            {quickSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div key={i} className="doc-start-card doc-glass" variants={itemVariants}>
                  <Icon size={24} />
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* DASHBOARD GUIDE */}
        <section className="doc-workspace-sector">
          <motion.h2 className="doc-sector-label" variants={itemVariants}>
            02. YOUR_DASHBOARD
          </motion.h2>

          <div className="doc-workspace-grid">
            {dashboardFeatures.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} className="doc-workspace-item" variants={itemVariants}>
                  <Icon size={20} />
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* HARDWARE HUB */}
        <section className="doc-hardware-sector">
          <motion.h2 className="doc-sector-label" variants={itemVariants}>
            03. HARDWARE_INTEGRATION
          </motion.h2>

          <div className="doc-hardware-grid">
            <motion.div className="doc-hardware-card doc-glass" variants={itemVariants}>
              <Cpu size={24} />
              <h3 style={{marginBottom: '2rem'}}>Supported Sensors</h3>

              <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                {hardwareGuide.map((h, i) => {
                  const Icon = h.icon;
                  return (
                    <div key={i} style={{display: 'flex', gap: '1rem', alignItems: 'flex-start'}}>
                      <div style={{padding: '0.5rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '4px', color: '#3b82f6'}}>
                        <Icon size={18} />
                      </div>
                      <div>
                        <h5 style={{fontSize: '0.85rem', fontWeight: '800', marginBottom: '0.25rem'}}>{h.title} ({h.sensor})</h5>
                        <p style={{fontSize: '0.8rem', color: '#94a3b8', margin: 0}}>{h.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div className="doc-hardware-card doc-glass" variants={itemVariants}>
              <Terminal size={24} />
              <h3>IoT Connection Code</h3>
              <p style={{marginBottom: '1.5rem', fontSize: '0.85rem'}}>Copy this simple code into your Arduino or ESP32 to start sending data.</p>

              <pre className="doc-terminal-body">
                <code>{codeSnippet}</code>
              </pre>
            </motion.div>
          </div>
        </section>

        {/* SECURITY NODES */}
        <section className="doc-security-sector">
          <motion.h2 className="doc-sector-label" variants={itemVariants}>
            04. SECURITY_TRUST
          </motion.h2>

          <div className="doc-security-grid">
            {securityNodes.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} className="doc-security-card doc-glass" variants={itemVariants}>
                  <Icon size={24} />
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* HELP FOOTER */}
        <motion.section className="doc-cta-sector" variants={itemVariants} style={{textAlign: 'center', margin: '4rem 0'}}>
          <p style={{color: '#94a3b8', marginBottom: '1.5rem'}}>Still have questions? Our clinical support team is here to help.</p>
          <div style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
            <Link to="/help" className="doc-btn-simple">Visit Help Center</Link>
            <Link to="/contact" className="doc-btn-simple active">Contact Support</Link>
          </div>
        </motion.section>
      </motion.main>

      <FooterSection />
    </div>
  );
};

export default Documentation;